import { lazy, Suspense, useEffect } from 'react'
import { createBrowserRouter, Navigate, useLocation, useNavigate } from 'react-router-dom'
import App from './App'
import ErrorPage from './app/error'
import RequirePlatformAdmin from '@/components/Platform/RequirePlatformAdmin'
import Splash from '@/components/splash/Splash'

// Pages
import HomePage from './app/home/page'
import OrdersPage from './app/orders/page'
import OrderDetailPage from './app/order/[id]/page'
import ProductsPage from './app/products/page'
import ProductRouterPage from './app/products/[sku]/page'
import ProductSkuPage from './app/products/sku/[sku]/page'
import ProductV2Page from './app/products/v2/[sku]/page'
import ProductBundlePage from './app/products/bundle/[sku]/page'
import StepToGlbPage from './app/products/step-to-glb/page'
import HatchPage from './app/products/hatch/page'
import LaserPage from './app/products/laser/page'
import CategoriesPage from './app/categories/page'
import PricingCreatePage from './app/pricing/create/page'
import ThemesPage from './app/themes/page'
import ThemeCreatePage from './app/themes/create/page'
import ThemeDetailPage from './app/theme/[id]/page'
import UsersPage from './app/users/page'
import RolesPage from './app/roles/page'
import MachinesPage from './app/machines/page'
import MediaPage from './app/media/page'
import SettingsPage from './app/settings/page'
import ReportingPage from './app/reporting/page'
import HelpPage from './app/help/page'
import AuthErrorPage from './app/auth-error/page'
import LoginPage from './app/_auth/login/page'
import SSOCallbackPage from './app/public/sso/page'
import { useSnapshot } from 'valtio'
import store from '@/store'
import { getLastVisitedPage } from '@/utils/lastVisitedPage'

// Loaded lazily so the chunk is never fetched for users without the platform
// admin role — they get redirected before the import is triggered.
const PlatformPage = lazy(() => import('./app/platform/page'))
// Product Profile V3 — parallel section-frame testbed, reachable by URL only.
const ProductV3Page = lazy(() => import('./app/products/v3/[sku]/page'))

// A lazy element needs a Suspense boundary of its own: without one the suspension
// escapes to the router, which treats it as a render failure and shows the error page.
const ProductV3Route = () => (
  <Suspense fallback={<Splash text="Loading..." />}>
    <ProductV3Page />
  </Suspense>
)

type LegacyTabRedirectProps = {
  pathname: string
  tab: string
}

const LegacyTabRedirect = ({ pathname, tab }: LegacyTabRedirectProps) => {
  const { search, hash } = useLocation()
  const searchParams = new URLSearchParams(search)

  searchParams.set('tab', tab)

  const queryString = searchParams.toString()
  const destination = `${pathname}${queryString ? `?${queryString}` : ''}${hash}`

  return <Navigate to={destination} replace />
}

const RootRedirect = () => {
  const navigate = useNavigate()
  const snap = useSnapshot(store)
  const userEmail = snap.currentUser?.email
  const selectedCompanyId = snap.currentUser?.selectedCompany?.id
  const selectedStoreName = snap.currentUser?.selectedCompany?.store_name

  useEffect(() => {
    const storedRedirect = sessionStorage.getItem('sso_redirect')
    let ssoRedirect: string | null = null

    if (storedRedirect) {
      sessionStorage.removeItem('sso_redirect')

      try {
        const redirectUrl = new URL(storedRedirect, window.location.origin)
        if (redirectUrl.origin === window.location.origin && redirectUrl.pathname !== '/') {
          ssoRedirect = `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`
        }
      } catch {
        // Ignore malformed stored redirects and use the default route.
      }
    }

    const lastVisitedPage = getLastVisitedPage(userEmail, selectedCompanyId, selectedStoreName)

    navigate(ssoRedirect || lastVisitedPage || '/orders', { replace: true })
  }, [navigate, userEmail, selectedCompanyId, selectedStoreName])

  return null
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <RootRedirect /> },
      { path: 'home', element: <HomePage /> },
      { path: 'orders', element: <OrdersPage /> },
      { path: 'order/:id', element: <OrderDetailPage /> },
      { path: 'products', element: <ProductsPage /> },
      { path: 'products/:sku', element: <ProductRouterPage /> },
      { path: 'products/sku/:sku', element: <ProductSkuPage /> },
      { path: 'products/v2/:sku', element: <ProductV2Page /> },
      { path: 'products/v3', element: <ProductV3Route /> },
      { path: 'products/v3/:sku', element: <ProductV3Route /> },
      { path: 'products/bundle/:sku', element: <ProductBundlePage /> },
      { path: 'products/step-to-glb', element: <StepToGlbPage /> },
      { path: 'products/hatch', element: <HatchPage /> },
      { path: 'products/laser', element: <LaserPage /> },
      { path: 'categories', element: <CategoriesPage /> },
      { path: 'pricing', element: <LegacyTabRedirect pathname="/products" tab="Pricing" /> },
      { path: 'pricing/create', element: <PricingCreatePage /> },
      { path: 'themes', element: <ThemesPage /> },
      { path: 'themes/create', element: <ThemeCreatePage /> },
      { path: 'theme/:id', element: <ThemeDetailPage /> },
      { path: 'users', element: <UsersPage /> },
      { path: 'roles', element: <RolesPage /> },
      { path: 'machines', element: <MachinesPage /> },
      { path: 'media', element: <MediaPage /> },
      { path: 'fonts', element: <LegacyTabRedirect pathname="/media" tab="Fonts" /> },
      { path: 'settings', element: <SettingsPage /> },
      {
        path: 'platform',
        element: (
          <RequirePlatformAdmin>
            <Suspense fallback={<Splash text="Loading..." />}>
              <PlatformPage />
            </Suspense>
          </RequirePlatformAdmin>
        ),
      },
      { path: 'reporting', element: <ReportingPage /> },
      { path: 'help', element: <HelpPage /> },
      { path: 'get-started', element: <Navigate to="/help" replace /> },
      { path: 'auth-error', element: <AuthErrorPage /> },
      { path: '_auth/login', element: <LoginPage /> },
      { path: 'public/sso', element: <SSOCallbackPage /> },
    ],
  },
])
